import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { DatalyLogo } from "@/components/DatalyLogo";
import { APP_DISPLAY_NAME } from "@/branding";
import { clearToken, fetchSession, getToken, type Session } from "@/lib/api";
import { Dashboard } from "@/pages/Dashboard";
import { Login } from "@/pages/Login";

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(() =>
    getToken() ? undefined : null,
  );
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getToken();
      if (!token) {
        setSession(null);
        return;
      }
      setSession(undefined);
      try {
        const s = await fetchSession();
        if (!cancelled) setSession(s);
      } catch {
        clearToken();
        if (!cancelled) setSession(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (session === undefined) {
    return (
      <AppShell>
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 text-[var(--color-text-muted)]">
          <DatalyLogo size="lg" className="rounded-xl" />
          <p className="text-sm font-medium text-[var(--color-text)]">{APP_DISPLAY_NAME}</p>
          <div className="flex items-center gap-2 text-xs text-[var(--color-text-subtle)]">
            <div
              className="h-3.5 w-3.5 rounded-full border-2 border-[var(--color-border)] spin"
              style={{ borderTopColor: "var(--color-accent)" }}
            />
            Loading…
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={session ? <Navigate to="/app" replace /> : <Login />} />
        <Route
          path="/app/*"
          element={session ? <Dashboard session={session} /> : <Navigate to="/" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
